// @ts-nocheck

import { Component } from '@theme/component';

/** Max time to wait for the intersection observer before forcing init anyway. */
const INTERSECTION_FALLBACK_MS = 4000;
/** Max time to wait for the global Swiper library before giving up on it. */
const SWIPER_WAIT_TIMEOUT_MS = 8000;

/**
 * Home main banner custom element. Initializes Swiper for multi-slide banners.
 *
 * @extends {Component}
 */
export class HomeMainBanner extends Component {
  /** @type {any} */
  #swiper;

  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  /** Pauses/resumes autoplay based on viewport visibility (INP). */
  /** @type {IntersectionObserver | undefined} */
  #autoplayVisibilityObserver;

  /** True once deferred autoplay has been unlocked by first user interaction. */
  #autoplayUnlocked = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #inViewFallbackTimer;

  /** @type {(() => void) | undefined} */
  #removeAutoplayListeners;

  connectedCallback() {
    super.connectedCallback();

    if (!this.hasAttribute('data-swiper')) return;

    this.#observeInView();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroy();
  }

  #observeInView() {
    if (!('IntersectionObserver' in window)) {
      this.#initSwiper();
      return;
    }

    const triggerInit = () => {
      clearTimeout(this.#inViewFallbackTimer);
      this.#inViewFallbackTimer = undefined;
      this.#intersectionObserver?.disconnect();
      this.#intersectionObserver = undefined;
      this.#initSwiper();
    };

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        triggerInit();
      },
      { root: null, rootMargin: '200px 0px', threshold: 0.01 }
    );

    this.#intersectionObserver.observe(this);

    // Safety net: a hidden ancestor, an unusual scroll container, or any other edge
    // case that keeps the observer from ever firing would otherwise strand the
    // fake slide in the DOM forever. Force init if we've waited long enough.
    this.#inViewFallbackTimer = setTimeout(triggerInit, INTERSECTION_FALLBACK_MS);
  }

  /**
   * @returns {Promise<typeof Swiper | undefined>}
   */
  #waitForSwiper() {
    return new Promise((resolve) => {
      const startedAt = Date.now();
      const check = () => {
        if (typeof Swiper !== 'undefined') {
          resolve(Swiper);
          return;
        }
        if (Date.now() - startedAt > SWIPER_WAIT_TIMEOUT_MS) {
          resolve(undefined);
          return;
        }
        setTimeout(check, 100);
      };
      check();
    });
  }

  async #initSwiper() {
    const SwiperCtor = await this.#waitForSwiper();
    if (!this.isConnected) return;

    // Always clean up the pre-init placeholder, even if Swiper never loaded or the
    // expected markup is missing, so it never stays stuck on screen.
    const fakeSlide = this.querySelector('.fake-slide');
    fakeSlide?.remove();
    const wrapper = this.querySelector('.swiper-wrapper');
    if (wrapper instanceof HTMLElement) wrapper.style.marginLeft = '0px';

    if (!SwiperCtor) return;

    const swiperEl = this.querySelector('.custom_main_banner');
    if (!(swiperEl instanceof HTMLElement) || !(wrapper instanceof HTMLElement)) return;

    const autoplayDelay = this.autoplayDelay;
    const paginationEl = this.querySelector('.swiper-pagination');
    const nextEl = this.querySelector('.swiper-button-next');
    const prevEl = this.querySelector('.swiper-button-prev');

    this.#swiper = new SwiperCtor(swiperEl, {
      initialSlide: this.#initialSlide,
      slidesPerView: 1.2,
      spaceBetween: 6,
      centeredSlides: true,
      loop: this.hasAttribute('loop'),
      grabCursor: true,
      autoplay: autoplayDelay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
          }
        : false,
      pagination: paginationEl
        ? {
            el: paginationEl,
            clickable: true,
          }
        : undefined,
      navigation:
        nextEl && prevEl
          ? {
              nextEl,
              prevEl,
            }
          : undefined,
      breakpoints: {
        320: {
          slidesPerView: 1.1,
          spaceBetween: 6,
          centeredSlides: true,
        },
        768: {
          slidesPerView: 1.3,
          spaceBetween: 15,
          centeredSlides: true,
        },
        1024: {
          slidesPerView: 1.8,
          spaceBetween: 20,
          centeredSlides: true,
        },
      },
      on: {
        autoplayTimeLeft: (_s, _time, progress) => {
          this.querySelectorAll('.swiper-pagination-bullet').forEach((bullet) => {
            if (bullet instanceof HTMLElement) bullet.style.setProperty('--progress', '0');
          });
          const activeBullet = this.querySelector('.swiper-pagination-bullet-active');
          if (activeBullet instanceof HTMLElement) {
            activeBullet.style.setProperty('--progress', String(1 - progress));
          }
        },
      },
    });

    if (autoplayDelay) this.#setupDeferredAutoplay();
  }

  #setupDeferredAutoplay() {
    const swiper = this.#swiper;
    if (!swiper?.autoplay) return;

    swiper.autoplay.stop();
    this.#autoplayUnlocked = false;
    this.#setupAutoplayVisibility();

    const startAutoplay = () => {
      const run = () => {
        this.#autoplayUnlocked = true;
        // Only run while banner is in view; visibility observer handles pause/resume after this.
        if (this.#isInViewport()) {
          swiper.autoplay?.start();
        }
        this.#removeAutoplayListeners?.();
      };

      if (window.requestIdleCallback) {
        requestIdleCallback(run);
      } else {
        setTimeout(run, 0);
      }
    };

    document.addEventListener('scroll', startAutoplay, { passive: true });
    document.addEventListener('mousemove', startAutoplay, { passive: true });
    document.addEventListener('click', startAutoplay, { once: true });
    document.addEventListener('touchstart', startAutoplay, { passive: true, capture: true });
    document.addEventListener('keydown', startAutoplay, { once: true });

    this.#removeAutoplayListeners = () => {
      document.removeEventListener('scroll', startAutoplay);
      document.removeEventListener('mousemove', startAutoplay);
      document.removeEventListener('click', startAutoplay);
      document.removeEventListener('touchstart', startAutoplay, { capture: true });
      document.removeEventListener('keydown', startAutoplay);
      this.#removeAutoplayListeners = undefined;
    };
  }

  /**
   * Pause autoplay when off-screen; resume when back in view (after unlock).
   * Keeps timers/rAF off the main thread while the banner is not visible.
   */
  #setupAutoplayVisibility() {
    if (!('IntersectionObserver' in window) || !this.#swiper?.autoplay) return;

    this.#autoplayVisibilityObserver?.disconnect();
    this.#autoplayVisibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (!this.#autoplayUnlocked || !this.#swiper?.autoplay) return;
        if (entry?.isIntersecting) {
          this.#swiper.autoplay.start();
        } else {
          this.#swiper.autoplay.stop();
        }
      },
      { root: null, rootMargin: '0px', threshold: 0.01 }
    );

    this.#autoplayVisibilityObserver.observe(this);
  }

  /** @returns {boolean} */
  #isInViewport() {
    const rect = this.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < (window.innerHeight || document.documentElement.clientHeight);
  }

  #destroy() {
    clearTimeout(this.#inViewFallbackTimer);
    this.#inViewFallbackTimer = undefined;
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = undefined;
    this.#autoplayVisibilityObserver?.disconnect();
    this.#autoplayVisibilityObserver = undefined;
    this.#autoplayUnlocked = false;
    this.#removeAutoplayListeners?.();
    this.#swiper?.destroy(true, true);
    this.#swiper = undefined;
  }

  /**
   * Starting slide index from `data-initial-slide` (homepage 0, cohort 1).
   * @returns {number}
   */
  get #initialSlide() {
    const value = parseInt(this.getAttribute('data-initial-slide') || '0', 10);
    if (Number.isNaN(value) || value < 0) return 0;
    return value;
  }

  /**
   * Autoplay delay in milliseconds, or undefined when disabled.
   * @returns {number | undefined}
   */
  get autoplayDelay() {
    if (!this.hasAttribute('autoplay')) return undefined;

    const value = parseInt(this.getAttribute('autoplay') || '', 10);
    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }
}

if (!customElements.get('home-main-banner-component')) {
  customElements.define('home-main-banner-component', HomeMainBanner);
}
